import usePxAuthContext from 'src/authentication/usePxAuthContext';
import Action from 'src/casl/Action';
import Subject from 'src/casl/Subject';
import useCurrentMetaverse from 'src/features/Dashboard/hooks/useCurrentMetaverse';
import Background from 'src/features/PixelStreaming/components/Background/Background';
import ContentViewer from 'src/features/PixelStreaming/components/ContentViewer/ContentViewer';
import HUD from 'src/features/PixelStreaming/components/HUD';
import ImageSlotModal from 'src/features/PixelStreaming/components/ImageSlot/ImageSlotUploadModal';
import PointsOfInterest from 'src/features/PixelStreaming/components/PointsOfInterest';
import SceneSlotEditModal from 'src/features/PixelStreaming/components/SceneSlot/SceneSlotEditModal';
import SettingsOverlay from 'src/features/PixelStreaming/components/SettingsOverlay';
import { usePixelStreamingContext } from 'src/features/PixelStreaming/contexts/PixelStreamingContext';
import useDebugEmitUIInteract from 'src/features/PixelStreaming/hooks/useDebugEmitUIInteraction';
import useInitialPacket from 'src/features/PixelStreaming/hooks/useInitialPacket';
import useJoinSpace from 'src/features/PixelStreaming/hooks/useJoinSpace';
import useOpenBooth from 'src/features/PixelStreaming/hooks/useOpenBooth';
import useOpenExternalLink from 'src/features/PixelStreaming/hooks/useOpenExternalLink';
import useOpenMap from 'src/features/PixelStreaming/hooks/useOpenMap';
import useOpenModal from 'src/features/PixelStreaming/hooks/useOpenModal';
import useProfileUpdate from 'src/features/PixelStreaming/hooks/useProfileUpdate';
import useRefreshToken from 'src/features/PixelStreaming/hooks/useRefreshToken';
import useUserJoinAndExit from 'src/features/PixelStreaming/hooks/useUserJoinAndExit';
import TimeoutModal from 'src/features/ScalablePixelStreaming/components/SpsTimeoutModal';
import UsageLimitReachedModal from 'src/features/Usage/components/UsageLimitReachedModal';
import UsageLimitReachedScreen from 'src/features/Usage/components/UsageLimitReachedScreen';
import { useUsageContext } from 'src/features/Usage/contexts/UsageContextProvider';
import UploadContentModalContainer from 'src/features/Visitor/components/UploadContentModal/UploadContentModalContainer';
import { ISpace, MetaverseResponse } from 'src/interfaces/IMetaverse';
import VisualSource from 'src/interfaces/VisualSource';
import { getMetaverseEditURL } from 'src/utils/URLHelper';

import defaultBackground from '../../../../../../assets/images/world_map_bg.jpg';
import styles from './Metaverse3DEditorInterlucentSession.module.scss';

interface Metaverse3DEditorInterlucentSessionProps {
  metaverse: MetaverseResponse;
  space: ISpace;
}

export default function Metaverse3DEditorInterlucentSession({
  metaverse,
  space,
}: Metaverse3DEditorInterlucentSessionProps) {
  const { user, authToken } = usePxAuthContext();
  const { metaverse: currentMetaverse, ability } = useCurrentMetaverse();

  const hasAdvancedUpload = ability.can(Action.Manage, Subject.AdvancedContentUpload);
  const canUploadImageSlot = ability.can(Action.Create, Subject.ImageSlot);

  const { _id: metaverseId } = metaverse;
  const { mapName } = space.environment;

  const { hasUsage } = useUsageContext();
  const { isStreaming } = usePixelStreamingContext();

  useInitialPacket(metaverseId, currentMetaverse?.organizationId, authToken, user);
  useRefreshToken();
  useOpenExternalLink();
  useJoinSpace(space._id);
  useOpenModal();
  useProfileUpdate();
  useDebugEmitUIInteract();
  useUserJoinAndExit();
  useOpenBooth();
  useOpenMap(mapName);

  const handleTimeout = () => {
    window.location.href = getMetaverseEditURL(metaverse);
  };

  const defaultBackgroundSource: VisualSource = { srcType: 'image', src: defaultBackground };

  return (
    <>
      {!isStreaming && <Background className={styles.background} source={space.background ?? defaultBackgroundSource} />}
      <UsageLimitReachedScreen isOwner logo={metaverse?.logo} />
      {hasUsage && (
        <>
          <HUD globeButtonEnabled={false} />
          <PointsOfInterest />
          <ContentViewer metaverse={metaverse} />
          <SettingsOverlay />
        </>
      )}
      <UploadContentModalContainer isSiteAdmin={hasAdvancedUpload} metaverseId={metaverseId} />
      <TimeoutModal onTimeout={handleTimeout} />
      <UsageLimitReachedModal isOwner onDismiss={handleTimeout} />
      <ImageSlotModal
        metaverseId={metaverse._id}
        spaceId={space._id}
        isSiteAdmin={canUploadImageSlot}
      />
      <SceneSlotEditModal
        metaverseId={metaverse._id}
        spaceId={space._id}
      />
    </>
  );
}
